import { Helmet } from 'react-helmet-async';

const SEO = ({ 
  title, 
  description, 
  keywords,
  canonicalPath = '',
  calculatorType = null,
  calculatorDescription = null,
  faqItems = null,
  article = null,
}) => {
  const baseUrl = 'https://www.paisapyara.com';
  const fullUrl = `${baseUrl}${canonicalPath}`;
  const socialImageUrl = `${baseUrl}/social-share.png`;
  const includeSocialTags = canonicalPath !== '/';
  const fullTitle = title ? `${title} - #Paisa Pyara` : '#Paisa Pyara - 100+ Indian calculators & bill generators, free & private';
  
  // JSON-LD Structured Data for Calculator pages
  const calculatorSchema = calculatorType ? {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": title,
    "description": calculatorDescription || description,
    "url": fullUrl,
    "applicationCategory": "FinanceApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "INR"
    },
    "featureList": [
      "Free to use",
      "No registration required",
      "Instant calculations",
      "PDF export",
      "Mobile friendly"
    ],
    "creator": {
      "@type": "Organization",
      "name": "#Paisa Pyara",
      "url": baseUrl
    }
  } : null;

  // BreadcrumbList Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": baseUrl
      },
      ...(canonicalPath ? [{
        "@type": "ListItem",
        "position": 2,
        "name": title,
        "item": fullUrl
      }] : [])
    ]
  };

  const articleSchema = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.description,
    "author": {
      "@type": "Organization",
      "name": "paisapyara"
    },
    "publisher": {
      "@type": "Organization",
      "name": "#Paisa Pyara",
      "logo": {
        "@type": "ImageObject",
        "url": socialImageUrl
      }
    },
    "datePublished": article.datePublished,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": fullUrl
    }
  } : null;

  // FAQPage Schema for calculators (generic financial questions)
  const faqSchema = faqItems?.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map((item) => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer,
      }
    })),
  } : calculatorType ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": `How to use ${title}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `Enter your values in the input fields and the ${title} will automatically calculate the results. You can also download the results as a PDF.`
        }
      },
      {
        "@type": "Question",
        "name": `Is the ${title} free to use?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, all calculators on paisapyara are completely free to use with no registration required."
        }
      }
    ]
  } : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullUrl} />

      {includeSocialTags && (
        <>
          {/* Open Graph */}
          <meta property="og:type" content="website" />
          <meta property="og:url" content={fullUrl} />
          <meta property="og:title" content={fullTitle} />
          <meta property="og:description" content={description} />
          <meta property="og:image" content={socialImageUrl} />
          <meta property="og:image:secure_url" content={socialImageUrl} />
          <meta property="og:image:type" content="image/png" />
          <meta property="og:image:width" content="1500" />
          <meta property="og:image:height" content="788" />
          <meta property="og:image:alt" content="#Paisa Pyara - Financial calculators and bill generators" />
          <meta property="og:site_name" content="#Paisa Pyara" />
          <meta property="og:locale" content="en_IN" />

          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:url" content={fullUrl} />
          <meta name="twitter:title" content={fullTitle} />
          <meta name="twitter:description" content={description} />
          <meta name="twitter:image" content={socialImageUrl} />
          <meta name="twitter:image:alt" content="#Paisa Pyara - Financial calculators and bill generators" />
        </>
      )}

      {/* JSON-LD Structured Data */}
      {calculatorSchema && (
        <script type="application/ld+json">
          {JSON.stringify(calculatorSchema)}
        </script>
      )}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;

